import pedidoVentaService, {
  PedidoVenta,
  PedidoVentaDetalle,
} from '../../../services/pedidoVentaService';
import { pedidoTieneCantidadB } from '../../../services/pedidoVentaConversionMaestros';
import { obtenerProductoParaDocumento } from '../../../utils/bloqueaArtDiscontinuados';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import productoService from '../../../services/productoService';
import {
  obtenerPrecioBaseProducto,
  resolverPrecioDesdeLista,
} from '../../../utils/precioListaPedido';
import {
  precioEnUmDocumento,
  umBaseProducto,
} from '../../../utils/umVentaDocumento';
import { POSLinea } from './types';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const calcularSubtotal = (l: Pick<POSLinea, 'cantidad' | 'precioUnitario' | 'descuento'>): number =>
  Math.round(l.cantidad * l.precioUnitario * (1 - l.descuento / 100) * 100) / 100;

const normalizarDescuentoPorcentaje = (detalle: PedidoVentaDetalle): number => {
  let porcDesc = Number(detalle.porcDesc ?? detalle.descuento ?? 0);
  if (porcDesc > 0 && porcDesc <= 1) porcDesc *= 100;
  if (!Number.isFinite(porcDesc)) return 0;
  return Math.max(0, Math.min(100, porcDesc));
};

const seleccionarUltimoPedido = (items: PedidoVenta[]): PedidoVenta | null => {
  const ordered = [...items].sort((a, b) => {
    const nroA = Number(a.nroFormulario || 0);
    const nroB = Number(b.nroFormulario || 0);
    return nroB - nroA;
  });
  return ordered.find((p) => Array.isArray(p.detalles) && p.detalles.length > 0) ?? null;
};

async function resolverPrecioActual(
  producto: unknown,
  nroLista?: string,
  umDocumento?: string,
): Promise<number> {
  let precio = obtenerPrecioBaseProducto(producto);
  let umPrecioLista = umBaseProducto(producto);
  const codigo = String((producto as { codigoArticulo?: string })?.codigoArticulo ?? '').trim();
  if (nroLista && codigo) {
    try {
      const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(nroLista, codigo);
      const resultado = resolverPrecioDesdeLista(producto, reg);
      if (resultado.tipo !== 'sinPrecio') {
        precio = resultado.precioLista;
        umPrecioLista = (reg.um || '').trim() || umPrecioLista;
      }
    } catch {
      /* sin precio en lista: usa precio base del producto */
    }
  }
  const umDoc = String(umDocumento ?? '').trim() || umBaseProducto(producto);
  const umBase = umBaseProducto(producto);
  if (codigo && umDoc && umPrecioLista) {
    try {
      const detalle = await productoService.getEquivalenciasUmDetalle(codigo);
      precio = precioEnUmDocumento(
        precio,
        umDoc,
        umBase,
        detalle.equivalencias ?? [],
        umPrecioLista,
      );
    } catch {
      /* sin equivalencias: precio sin convertir */
    }
  }
  return Number.isFinite(precio) ? precio : 0;
}

export type RepetirUltimoPedidoOk = {
  ok: true;
  lineas: POSLinea[];
  omitidas: number;
  nroFormulario: number;
  /** El último pedido tiene Cantidad B (conversión hecha). */
  tieneCantidadB: boolean;
  productosIva: Record<string, unknown>;
};

export type RepetirUltimoPedidoFail = {
  ok: false;
  reason: 'sin_cliente' | 'sin_historial' | 'sin_productos';
  message: string;
};

export type RepetirUltimoPedidoResult = RepetirUltimoPedidoOk | RepetirUltimoPedidoFail;

export type RepetirUltimoPedidoParams = {
  nroCliente: number;
  nroLista?: string;
};

/**
 * Obtiene el último pedido del cliente y arma líneas POS con precios vigentes
 * (no reutiliza el precio histórico del pedido).
 */
export async function construirLineasDesdeUltimoPedido(
  params: RepetirUltimoPedidoParams,
): Promise<RepetirUltimoPedidoResult> {
  const nroCliente = Number(params.nroCliente) || 0;
  if (nroCliente <= 0) {
    return {
      ok: false,
      reason: 'sin_cliente',
      message: 'Seleccioná un cliente para usar esta opción.',
    };
  }

  const page = await pedidoVentaService.getPedidosVenta({
    nroCliente: String(nroCliente),
    pageNumber: 1,
    pageSize: 20,
  });

  const ultimo = seleccionarUltimoPedido(page.items || []);
  if (!ultimo) {
    return {
      ok: false,
      reason: 'sin_historial',
      message: 'El cliente no tiene un pedido previo para repetir.',
    };
  }

  const nroLista = String(params.nroLista || '').trim() || undefined;
  const lineas: POSLinea[] = [];
  const productosIva: Record<string, unknown> = {};
  let omitidas = 0;

  for (const detalle of ultimo.detalles || []) {
    const codigoArticulo = String(detalle.codigoArticulo || '').trim();
    if (!codigoArticulo) {
      omitidas += 1;
      continue;
    }

    const cantidad = Math.max(0, Number(detalle.cantidad || 0));
    if (cantidad <= 0) {
      omitidas += 1;
      continue;
    }

    let producto: unknown;
    try {
      producto = await obtenerProductoParaDocumento(codigoArticulo, 'VENTAS');
    } catch {
      omitidas += 1;
      continue;
    }

    const descuento = normalizarDescuentoPorcentaje(detalle);
    const descripcionArticulo =
      String((producto as { descripcion?: string })?.descripcion || '').trim()
      || String(detalle.descripcionArticulo || '').trim()
      || codigoArticulo;
    const um = String(
      (producto as { um?: string; unidadMedida?: string; idUnidadMedida?: string })?.um
      ?? (producto as { unidadMedida?: string })?.unidadMedida
      ?? detalle.um
      ?? '',
    ).trim();
    const precioUnitario = await resolverPrecioActual(producto, nroLista, um);
    if (!Number.isFinite(precioUnitario) || precioUnitario <= 0) {
      omitidas += 1;
      continue;
    }

    const linea: POSLinea = {
      id: genId(),
      codigoArticulo,
      descripcionArticulo,
      cantidad,
      precioUnitario,
      descuento,
      descuentoManual: true,
      subtotal: 0,
      um,
      notas: detalle.notas,
    };
    linea.subtotal = calcularSubtotal(linea);
    lineas.push(linea);
    productosIva[codigoArticulo] = producto;
  }

  if (lineas.length === 0) {
    return {
      ok: false,
      reason: 'sin_productos',
      message: 'No se pudieron mapear productos del último pedido al catálogo actual.',
    };
  }

  return {
    ok: true,
    lineas,
    omitidas,
    nroFormulario: Number(ultimo.nroFormulario) || 0,
    tieneCantidadB: pedidoTieneCantidadB(ultimo),
    productosIva,
  };
}
